import React from "react";
import { Link } from "react-router-dom";
import {
  GearSix, Bug, HardDrives, Desktop, WifiHigh, Wrench, Buildings, ArrowUpRight,
} from "@phosphor-icons/react";

const SERVICES = [
  {
    icon: GearSix,
    title: "PC Tune-Up",
    desc: "Speed up a sluggish computer — clear junk, optimize startup, update drivers, and restore like-new performance.",
    points: ["Boot & shutdown optimization", "Temp file & registry cleanup", "Driver & OS updates"],
    href: "/computer-repair",
  },
  {
    icon: Bug,
    title: "Malware & Virus Removal",
    desc: "Deep scans remove trojans, ransomware, spyware, and adware. We harden your system so it doesn't come back.",
    points: ["Rootkit & ransomware removal", "Browser hijack repair", "Real-time protection setup"],
    href: "/virus-removal",
  },
  {
    icon: HardDrives,
    title: "Data Backup & Recovery",
    desc: "Protect photos, work files, and tax docs with automated, encrypted cloud & local backups — plus recovery.",
    points: ["3-2-1 backup strategy", "Cloud + external sync", "Lost file recovery"],
    href: "/data-recovery",
  },
  {
    icon: Desktop,
    title: "Remote Support",
    desc: "Screen-share help in minutes — no waiting rooms. Perfect for urgent software glitches and setup help.",
    points: ["Same-day sessions", "Windows, macOS, Linux", "Flat per-session pricing"],
    href: "/remote-it-support",
  },
  {
    icon: WifiHigh,
    title: "Network & Wi-Fi Setup",
    desc: "Fast, reliable Wi-Fi across every room. Mesh systems, guest networks, printer & smart-home integration.",
    points: ["Mesh Wi-Fi install", "VLAN & guest network", "Printer & IoT setup"],
    href: "/wifi-optimization",
  },
  {
    icon: Wrench,
    title: "Hardware Repair & Upgrades",
    desc: "SSD upgrades, RAM boosts, screen replacements, and battery swaps — parts sourced and fitted by certified techs.",
    points: ["SSD / RAM upgrades", "Laptop screen repair", "Battery replacement"],
    href: "/laptop-repair",
  },
  {
    icon: Buildings,
    title: "Business IT Support",
    desc: "Proactive monitoring, security, and help-desk for SMBs. Keep your team productive with flat per-user MSP plans starting at $49/user/mo.",
    points: ["Managed endpoints", "Email & domain security", "On-site + remote support"],
    href: "/managed-it-services",
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="bg-white pt-32 md:pt-40 pb-20 md:pb-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-sm font-semibold uppercase tracking-widest text-blue-600">What we do</div>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900">
            Seven core services. Fifteen dedicated solutions.
          </h2>
          <p className="mt-4 text-lg text-zinc-600 leading-relaxed">
            From a single slow laptop to a 50-seat office rollout — we cover the full spectrum of IT care with transparent pricing and same-week availability.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Link
                key={s.title}
                to={s.href}
                data-testid={`service-card-${i}`}
                className="group relative bg-zinc-50 border border-zinc-200 rounded-2xl p-8 hover:-translate-y-1 hover:shadow-xl hover:border-blue-300 hover:bg-white transition-all duration-300"
              >
                <div className="h-12 w-12 rounded-xl bg-blue-600/10 text-blue-600 grid place-items-center">
                  <Icon size={28} weight="duotone" />
                </div>
                <h3 className="mt-6 font-heading text-xl font-semibold text-zinc-900">{s.title}</h3>
                <p className="mt-3 text-zinc-600 leading-relaxed text-[15px]">{s.desc}</p>
                <ul className="mt-5 space-y-2 text-sm text-zinc-700">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-600 shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
                <span
                  data-testid={`service-cta-${i}`}
                  className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-blue-600 group-hover:gap-2 transition-all"
                >
                  Learn more
                  <ArrowUpRight size={16} weight="bold" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
